// Shared signal for sending a prompt into the currently-open project chat (ProjectChat) from
// elsewhere in the app — e.g. clicking one of the example questions in the Help overlay should drop
// that question into the chat and send it. ProjectChat owns the conversation state, so we notify it
// via a same-tab custom event carrying the prompt text. Only relevant while a project chat is
// mounted; if none is listening the request is simply a no-op.

const CHAT_PROMPT_EVENT = 'loopqa:chat-prompt'

// Ask the open project chat to send `text` as the next user turn.
export function requestChatPrompt(text: string) {
  try {
    window.dispatchEvent(new CustomEvent<string>(CHAT_PROMPT_EVENT, { detail: text }))
  } catch {
    /* ignore */
  }
}

export function onChatPromptRequest(handler: (text: string) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<string>).detail)
  window.addEventListener(CHAT_PROMPT_EVENT, listener)
  return () => window.removeEventListener(CHAT_PROMPT_EVENT, listener)
}

// A softer variant: drop `text` into the composer WITHOUT sending it, so the user can review/complete
// it and hit send themselves. Backs the read-only settings page's "Change in chat" deep-links — the
// page hands off the intent ("Change the budget"), the user supplies the value in chat.
const CHAT_PROMPT_FILL_EVENT = 'loopqa:chat-prompt-fill'

export function requestChatPromptFill(text: string) {
  try {
    window.dispatchEvent(new CustomEvent<string>(CHAT_PROMPT_FILL_EVENT, { detail: text }))
  } catch {
    /* ignore */
  }
}

export function onChatPromptFillRequest(handler: (text: string) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<string>).detail)
  window.addEventListener(CHAT_PROMPT_FILL_EVENT, listener)
  return () => window.removeEventListener(CHAT_PROMPT_FILL_EVENT, listener)
}

// When the deep-link fires from a surface with NO chat mounted (the standalone settings tab), the live
// fill event has no listener. We stash the prompt here and the chat consumes it on its next mount —
// after the caller navigates to a chat-bearing route. Cleared on read so it only fills once.
let pendingChatPrompt: string | null = null

export function setPendingChatPrompt(text: string) {
  pendingChatPrompt = text
}

export function takePendingChatPrompt(): string | null {
  const p = pendingChatPrompt
  pendingChatPrompt = null
  return p
}
